import { useEffect, useMemo, useState } from 'react';
import { useOrg } from '../app/OrgContext.js';
import { currencySymbol } from '@goss/types';
import { apiFetch } from '../lib/api.js';
import { C, alpha } from '../lib/theme.js';
import { Card, PageHeader, StatusPill, InfoTip } from '../ui/ui.js';
import { METRIC_INFO as M } from '../lib/metricInfo.js';
import type {
  MetricRow,
  MetricsResponse,
  MetaEntity,
  EntitiesResponse,
  Connections,
} from '../lib/types.js';

/* Live per-campaign / per-adset Meta insights (metrics_daily). */

function abbrev(n: number): string {
  const a = Math.abs(n);
  if (a >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (a >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
// ISO → "12 Jul 2026" (null when missing/unparseable), for launch / end dates.
function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return new Date(t).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

interface Agg {
  id: string;
  name: string;
  campaignId: string | null;
  currency: string;
  spend: number;
  impressions: number;
  reach: number;
  // Two click families, always shown side by side: `clicks` is Meta's clicks(all)
  // — link taps PLUS likes, comments, profile taps, image expands — while
  // `linkClicks` is inline_link_clicks, the only one that means "went to the site".
  clicks: number;
  linkClicks: number | null;
  ctr: number | null;
  cpc: number | null;
  linkCtr: number | null;
  linkCpc: number | null;
  hook: number | null;
  hold: number | null;
  freq: number | null;
  _hookSum: number;
  _hookN: number;
  _holdSum: number;
  _holdN: number;
  _freqSum: number;
  _freqN: number;
}

function aggregate(rows: MetricRow[]): Agg[] {
  const m = new Map<string, Agg>();
  for (const r of rows) {
    let a = m.get(r.entity_id);
    if (!a) {
      a = {
        id: r.entity_id,
        name: r.name ?? r.entity_id,
        campaignId: r.meta_campaign_id,
        currency: r.currency ?? '',
        spend: 0,
        impressions: 0,
        reach: 0,
        clicks: 0,
        linkClicks: null,
        ctr: null,
        cpc: null,
        linkCtr: null,
        linkCpc: null,
        hook: null,
        hold: null,
        freq: null,
        _hookSum: 0,
        _hookN: 0,
        _holdSum: 0,
        _holdN: 0,
        _freqSum: 0,
        _freqN: 0,
      };
      m.set(r.entity_id, a);
    }
    a.spend += Number(r.spend) || 0;
    a.impressions += Number(r.impressions) || 0;
    a.reach += Number(r.reach) || 0;
    a.clicks += Number(r.clicks) || 0;
    // Stay null when Meta never reported link clicks for this window — a real
    // zero and "no data" must not render as the same "0".
    if (r.link_clicks != null) a.linkClicks = (a.linkClicks ?? 0) + (Number(r.link_clicks) || 0);
    if (r.hook_rate != null) {
      a._hookSum += r.hook_rate;
      a._hookN++;
    }
    if (r.hold_rate != null) {
      a._holdSum += r.hold_rate;
      a._holdN++;
    }
    if (r.frequency != null) {
      a._freqSum += r.frequency;
      a._freqN++;
    }
    if ((!a.name || a.name === a.id) && r.name) a.name = r.name;
  }
  const out = [...m.values()];
  for (const a of out) {
    a.ctr = a.impressions ? (a.clicks / a.impressions) * 100 : null;
    a.cpc = a.clicks ? a.spend / a.clicks : null;
    a.linkCtr = a.impressions && a.linkClicks != null ? (a.linkClicks / a.impressions) * 100 : null;
    a.linkCpc = a.linkClicks ? a.spend / a.linkClicks : null;
    a.hook = a._hookN ? a._hookSum / a._hookN : null;
    a.hold = a._holdN ? a._holdSum / a._holdN : null;
    a.freq = a._freqN ? a._freqSum / a._freqN : null;
  }
  return out.sort((x, y) => y.spend - x.spend);
}

// Meta effective_status (+ ad-set learning stage) → a StatusPill keyword + label.
function statusPill(
  eff: string | null | undefined,
  learning: boolean,
): { status: string; label: string } | null {
  const e = (eff ?? '').toUpperCase();
  if (!e) return null;
  if (e === 'ACTIVE')
    return learning
      ? { status: 'learning', label: 'LEARNING' }
      : { status: 'active', label: 'ACTIVE' };
  if (e.includes('PAUSED')) return { status: 'paused', label: 'PAUSED' };
  if (e === 'IN_PROCESS' || e === 'PENDING_REVIEW' || e === 'PENDING_BILLING_INFO')
    return { status: 'watch', label: 'REVIEW' };
  if (e === 'WITH_ISSUES' || e === 'DISAPPROVED') return { status: 'error', label: 'ISSUES' };
  if (e === 'ARCHIVED') return { status: 'paused', label: 'ARCHIVED' };
  if (e === 'DELETED') return { status: 'paused', label: 'DELETED' };
  if (e === 'COMPLETED') return { status: 'paused', label: 'COMPLETED' };
  return { status: 'watch', label: e.replace(/_/g, ' ') };
}

// RULES §6 thresholds → cell color.
function hookColor(h: number | null): string {
  if (h == null) return C.faint;
  return h >= 30 ? C.good : h >= 20 ? C.watch : C.act;
}
function freqColor(f: number | null): string {
  if (f == null) return C.secondary;
  return f > 3 ? C.act : f > 2.5 ? C.watch : C.secondary;
}

const COLS = 'minmax(180px,1.6fr) 100px 100px 84px 74px 82px 80px 86px 60px 60px 56px';

// The click columns sort by the link figure — that's the primary number in the
// cell; clicks(all) rides underneath it as context.
type SortKey =
  | 'name'
  | 'launched'
  | 'spend'
  | 'reach'
  | 'linkClicks'
  | 'linkCtr'
  | 'linkCpc'
  | 'hook'
  | 'hold'
  | 'freq';

function SortHeader({
  k,
  label,
  info,
  align = 'right',
  sort,
  setSort,
}: {
  k: SortKey;
  label: string;
  info?: keyof typeof M;
  align?: 'left' | 'right';
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  setSort: (s: { key: SortKey; dir: 'asc' | 'desc' }) => void;
}) {
  const active = sort.key === k;
  return (
    <div
      onClick={() =>
        setSort(
          active
            ? { key: k, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
            : { key: k, dir: k === 'name' ? 'asc' : 'desc' },
        )
      }
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        userSelect: 'none',
        color: active ? C.secondary : C.muted,
      }}
    >
      {label}
      {active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
      {info && <InfoTip title={M[info].title} text={M[info].text} />}
    </div>
  );
}

export function Campaigns() {
  const { orgId, activeOrg } = useOrg();
  const [campaignRows, setCampaignRows] = useState<MetricRow[] | null>(null);
  const [adsetRows, setAdsetRows] = useState<MetricRow[] | null>(null);
  const [campaignEntities, setCampaignEntities] = useState<MetaEntity[]>([]);
  const [adsetEntities, setAdsetEntities] = useState<MetaEntity[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [adAccount, setAdAccount] = useState<string>('');
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [confirmDelId, setConfirmDelId] = useState<string | null>(null);
  const [confirmStatusId, setConfirmStatusId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [statusOverride, setStatusOverride] = useState<Record<string, string>>({});
  // Inline rename: which campaign is being edited, the draft text, and a local
  // name override so the new name shows instantly (before the next metrics sync).
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const [nameOverride, setNameOverride] = useState<Record<string, string>>({});
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const [days, setDays] = useState(90);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'paused' | 'completed'>(
    'active',
  );
  const [query, setQuery] = useState('');
  // Ad thumbnails from Meta: one per campaign (first ad) + one per ad set, so a
  // campaign mixing creatives shows a distinct image on each ad-set row.
  const [thumbBy, setThumbBy] = useState<Record<string, string>>({});
  const [adsetThumbBy, setAdsetThumbBy] = useState<Record<string, string>>({});
  const [syncing, setSyncing] = useState(false);
  const [refresh, setRefresh] = useState(0);

  // Pull fresh Meta data on demand (marketer+), then reload the page's data. Lets
  // a just-launched campaign show up without waiting for the daily 01:00 UTC cron.
  async function syncNow() {
    if (!orgId || syncing) return;
    setSyncing(true);
    setError(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/meta/sync`, { method: 'POST' });
      setRefresh((r) => r + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  async function setCampaignStatus(id: string, status: 'ACTIVE' | 'PAUSED') {
    setBusyId(id);
    setActionMsg(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/meta/campaign/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      });
      setStatusOverride((m) => ({ ...m, [id]: status }));
      setActionMsg(status === 'PAUSED' ? 'Paused in Meta.' : 'Live in Meta — now spending.');
    } catch (e) {
      setActionMsg(e instanceof Error ? e.message : 'Status change failed');
    } finally {
      setBusyId(null);
    }
  }
  async function renameCampaign(id: string) {
    const v = renameVal.trim();
    if (!v) return;
    setBusyId(id);
    setActionMsg(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/meta/campaign/${id}/name`, {
        method: 'POST',
        body: JSON.stringify({ name: v }),
      });
      setNameOverride((m) => ({ ...m, [id]: v }));
      setRenameId(null);
      setActionMsg('Campaign renamed in Meta.');
    } catch (e) {
      setActionMsg(e instanceof Error ? e.message : 'Rename failed');
    } finally {
      setBusyId(null);
    }
  }
  async function removeCampaign(id: string) {
    setBusyId(id);
    setActionMsg(null);
    setConfirmDelId(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/meta/campaign/${id}`, { method: 'DELETE' });
      setDeletedIds((s) => new Set(s).add(id));
      setActionMsg('Campaign deleted in Meta.');
    } catch (e) {
      setActionMsg(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusyId(null);
    }
  }

  useEffect(() => {
    if (!orgId) return;
    setCampaignRows(null);
    setAdsetRows(null);
    setCampaignEntities([]);
    setAdsetEntities([]);
    setError(null);
    Promise.all([
      apiFetch<MetricsResponse>(`/api/orgs/${orgId}/metrics?level=campaign&days=${days}`),
      apiFetch<MetricsResponse>(`/api/orgs/${orgId}/metrics?level=adset&days=${days}`),
      apiFetch<EntitiesResponse>(`/api/orgs/${orgId}/entities?level=campaign`),
      apiFetch<EntitiesResponse>(`/api/orgs/${orgId}/entities?level=adset`),
    ])
      .then(([c, a, ce, ae]) => {
        setCampaignRows(c.rows);
        setAdsetRows(a.rows);
        setCampaignEntities(ce.rows);
        setAdsetEntities(ae.rows);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load campaigns'));
    // Ad account number → deep links into Meta Ads Manager.
    apiFetch<Connections>(`/api/orgs/${orgId}/connections`)
      .then((d) => setAdAccount((d.meta.ad_account_id ?? '').replace(/^act_/, '')))
      .catch(() => {});
    // Ad thumbnails (campaign + ad-set), straight from Meta — one call.
    apiFetch<{ thumbs: Record<string, string>; adsetThumbs: Record<string, string> }>(
      `/api/orgs/${orgId}/campaign-thumbs`,
    )
      .then((d) => {
        setThumbBy(d.thumbs ?? {});
        setAdsetThumbBy(d.adsetThumbs ?? {});
      })
      .catch(() => {});
  }, [orgId, days, refresh]);

  // entity_id → current state; plus the set of campaigns with a learning ad set.
  const campaignStatus = useMemo(
    () => new Map(campaignEntities.map((e) => [e.entity_id, e])),
    [campaignEntities],
  );
  const adsetStatus = useMemo(
    () => new Map(adsetEntities.map((e) => [e.entity_id, e])),
    [adsetEntities],
  );
  const learningCampaigns = useMemo(() => {
    const s = new Set<string>();
    for (const e of adsetEntities) {
      if (e.learning_stage === 'LEARNING' && e.meta_campaign_id) s.add(e.meta_campaign_id);
    }
    return s;
  }, [adsetEntities]);

  // A campaign whose SCHEDULE has ended reads "Completed" in Meta even while its
  // on/off status is still ACTIVE. Detect it from the campaign's stop_time or —
  // when all its ad sets have an end_time in the past.
  const completedCampaigns = useMemo(() => {
    const now = Date.now();
    const past = (t: string | null) => !!t && Date.parse(t) < now;
    const adsetsByC = new Map<string, MetaEntity[]>();
    for (const a of adsetEntities) {
      if (!a.meta_campaign_id) continue;
      if (!adsetsByC.has(a.meta_campaign_id)) adsetsByC.set(a.meta_campaign_id, []);
      adsetsByC.get(a.meta_campaign_id)!.push(a);
    }
    const s = new Set<string>();
    for (const c of campaignEntities) {
      if ((c.effective_status ?? '').toUpperCase() !== 'ACTIVE') continue;
      const adsets = adsetsByC.get(c.entity_id) ?? [];
      const allAdsetsEnded = adsets.length > 0 && adsets.every((a) => past(a.end_time));
      if (past(c.end_time) || allAdsetsEnded) s.add(c.entity_id);
    }
    return s;
  }, [campaignEntities, adsetEntities]);

  // Default: most recent launches at the top (matches the "what did I just do" mental
  // model); the operator can re-sort by any metric column.
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'launched',
    dir: 'desc',
  });

  const campaigns = useMemo(() => {
    const arr = campaignRows ? aggregate(campaignRows) : [];
    // A just-launched campaign has no metrics yet, so it wouldn't appear even after
    // a sync. Add ONLY campaigns that are genuinely trying to deliver (active / in
    // review) but have no metrics, so a live-but-not-yet-delivering campaign still
    // shows (with "—" numbers) instead of vanishing. Everything else with no metrics
    // (paused, archived, schedule-completed) stays hidden — we don't flood the table.
    const withMetrics = new Set(arr.map((a) => a.id));
    const RUNNING = new Set(['ACTIVE', 'PENDING_REVIEW', 'IN_PROCESS', 'PENDING_BILLING_INFO']);
    for (const e of campaignEntities) {
      if (withMetrics.has(e.entity_id)) continue;
      if (completedCampaigns.has(e.entity_id)) continue; // schedule ended
      if (!RUNNING.has((e.effective_status ?? '').toUpperCase())) continue;
      arr.push({
        id: e.entity_id,
        name: e.name ?? e.entity_id,
        campaignId: e.entity_id,
        currency: '',
        spend: 0,
        impressions: 0,
        reach: 0,
        clicks: 0,
        linkClicks: null,
        ctr: null,
        cpc: null,
        linkCtr: null,
        linkCpc: null,
        hook: null,
        hold: null,
        freq: null,
        _hookSum: 0,
        _hookN: 0,
        _holdSum: 0,
        _holdN: 0,
        _freqSum: 0,
        _freqN: 0,
      });
    }
    const { key, dir } = sort;
    arr.sort((a, b) => {
      let cmp: number;
      if (key === 'name') cmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      else if (key === 'launched') {
        // Launch date lives on the campaign entity (ISO strings sort chronologically).
        // Unknown dates sort last in both directions so they don't crowd the top.
        const ax = campaignStatus.get(a.id)?.start_time ?? null;
        const bx = campaignStatus.get(b.id)?.start_time ?? null;
        if (ax === bx) cmp = 0;
        else if (!ax) return 1;
        else if (!bx) return -1;
        else cmp = ax.localeCompare(bx);
      } else cmp = (a[key] ?? -Infinity) - (b[key] ?? -Infinity);
      return dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [campaignRows, sort, campaignStatus, campaignEntities, completedCampaigns]);

  const adsetsByCampaign = useMemo(() => {
    const map = new Map<string, Agg[]>();
    if (!adsetRows) return map;
    for (const a of aggregate(adsetRows)) {
      const key = a.campaignId ?? '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    return map;
  }, [adsetRows]);

  // Each campaign's coarse state, for the filter tabs (mirrors the row logic).
  const statusKindOf = (id: string): 'active' | 'paused' | 'completed' | 'other' => {
    if (!statusOverride[id] && completedCampaigns.has(id)) return 'completed';
    const e = (statusOverride[id] ?? campaignStatus.get(id)?.effective_status ?? '').toUpperCase();
    if (e.includes('ACTIVE')) return 'active';
    if (e.includes('PAUSED')) return 'paused';
    return 'other';
  };
  const live = campaigns.filter((c) => !deletedIds.has(c.id));
  const counts = {
    all: live.length,
    active: live.filter((c) => statusKindOf(c.id) === 'active').length,
    paused: live.filter((c) => statusKindOf(c.id) === 'paused').length,
    completed: live.filter((c) => statusKindOf(c.id) === 'completed').length,
  };
  const needle = query.trim().toLowerCase();
  const visibleCampaigns = live
    .filter((c) => statusFilter === 'all' || statusKindOf(c.id) === statusFilter)
    .filter((c) => !needle || c.name.toLowerCase().includes(needle));

  const sym = currencySymbol(campaigns[0]?.currency ?? activeOrg?.default_currency);
  const loading = campaignRows === null && !error;

  return (
    <div style={{ padding: '26px 30px 40px', animation: 'basilfade .3s ease' }}>
      <PageHeader
        eyebrow={
          campaignRows
            ? `${campaigns.length} campaigns · META · last ${days} days`
            : `META · last ${days} days`
        }
        title="Campaigns"
        actions={
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Pull fresh Meta data now (don't wait for the daily sync) */}
            <button
              type="button"
              onClick={() => void syncNow()}
              disabled={syncing}
              title="Pull the latest campaigns + metrics from Meta now"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '6px 12px',
                borderRadius: 8,
                border: `1px solid ${C.line}`,
                background: C.surface2,
                color: syncing ? C.muted : C.secondary,
                cursor: syncing ? 'default' : 'pointer',
                font: `600 11px 'IBM Plex Mono',monospace`,
                fontFamily: 'inherit',
              }}
            >
              {syncing && (
                <span
                  style={{
                    width: 11,
                    height: 11,
                    border: `2px solid ${alpha(C.basil, 30)}`,
                    borderTopColor: C.basil,
                    borderRadius: 11,
                    display: 'inline-block',
                    animation: 'basilspin .7s linear infinite',
                  }}
                />
              )}
              {syncing ? 'Syncing…' : '↻ Sync now'}
            </button>
            {/* Date range */}
            <div
              style={{
                display: 'flex',
                border: `1px solid ${C.line}`,
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              {[7, 30, 90].map((d) => {
                const on = days === d;
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDays(d)}
                    style={{
                      padding: '5px 11px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      font: `600 11px 'IBM Plex Mono',monospace`,
                      border: 'none',
                      background: on ? C.surface2 : 'transparent',
                      color: on ? C.primary : C.muted,
                    }}
                  >
                    {d}D
                  </button>
                );
              })}
            </div>
            <div
              style={{
                display: 'flex',
                gap: 14,
                font: `500 10.5px 'IBM Plex Mono',monospace`,
                color: C.muted,
                alignItems: 'center',
              }}
            >
              <Legend color={C.good} label="GOOD" />
              <Legend color={C.watch} label="WATCH" />
              <Legend color={C.act} label="ACT" />
            </div>
          </div>
        }
      />

      {error && <Card style={{ padding: '16px 20px', color: C.act, fontSize: 13 }}>{error}</Card>}
      {actionMsg && (
        <Card style={{ padding: '10px 16px', color: C.secondary, fontSize: 13, marginBottom: 12 }}>
          {actionMsg}
        </Card>
      )}
      {loading && (
        <div style={{ color: C.muted, font: `500 12px 'IBM Plex Mono',monospace` }}>LOADING…</div>
      )}
      {campaignRows && campaigns.length === 0 && !error && (
        <Card style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 13 }}>
          No campaign data in the last {days} days. Hit “↻ Sync now” above to pull the latest from
          Meta, or check the account has spend in this window.
        </Card>
      )}

      {campaigns.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: 14,
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(
              [
                { key: 'all', label: 'All' },
                { key: 'active', label: 'Active' },
                { key: 'paused', label: 'Paused' },
                { key: 'completed', label: 'Completed' },
              ] as const
            ).map((t) => {
              const on = statusFilter === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setStatusFilter(t.key)}
                  style={{
                    padding: '6px 13px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 12,
                    fontWeight: 600,
                    border: `1px solid ${on ? C.basil : C.line}`,
                    background: on ? alpha(C.basil, 12) : C.surface,
                    color: on ? C.basil : C.secondary,
                  }}
                >
                  {t.label}{' '}
                  <span style={{ color: on ? C.basil : C.faint, fontWeight: 500 }}>
                    {counts[t.key]}
                  </span>
                </button>
              );
            })}
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search campaigns…"
            style={{
              background: C.surface,
              border: `1px solid ${C.line}`,
              borderRadius: 8,
              padding: '7px 12px',
              fontSize: 12.5,
              color: C.primary,
              fontFamily: 'inherit',
              minWidth: 200,
            }}
          />
        </div>
      )}

      {campaigns.length > 0 && (
        <Card style={{ overflow: 'hidden', background: 'transparent' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: COLS,
              alignItems: 'center',
              padding: '11px 18px',
              background: C.surface2,
              borderBottom: `1px solid ${C.line}`,
              font: `500 10px 'IBM Plex Mono',monospace`,
              letterSpacing: '.1em',
              color: C.muted,
            }}
          >
            <SortHeader k="name" label="CAMPAIGN" align="left" sort={sort} setSort={setSort} />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              STATUS
              <InfoTip title={M.status.title} text={M.status.text} />
            </div>
            <SortHeader k="launched" label="LAUNCHED" sort={sort} setSort={setSort} />
            <SortHeader k="spend" label="SPEND" info="spend" sort={sort} setSort={setSort} />
            <SortHeader k="reach" label="REACH" info="reach" sort={sort} setSort={setSort} />
            <SortHeader k="linkClicks" label="CLICKS" info="clicks" sort={sort} setSort={setSort} />
            <SortHeader k="linkCtr" label="CTR" info="ctr" sort={sort} setSort={setSort} />
            <SortHeader k="linkCpc" label="CPC" info="cpc" sort={sort} setSort={setSort} />
            <SortHeader k="hook" label="HOOK" info="hook" sort={sort} setSort={setSort} />
            <SortHeader k="hold" label="HOLD" info="hold" sort={sort} setSort={setSort} />
            <SortHeader k="freq" label="FREQ" info="frequency" sort={sort} setSort={setSort} />
          </div>

          {visibleCampaigns.length === 0 && (
            <div
              style={{ padding: '28px 18px', textAlign: 'center', color: C.muted, fontSize: 13 }}
            >
              No campaigns match this filter.
            </div>
          )}

          {visibleCampaigns.map((c) => {
            const adsets = adsetsByCampaign.get(c.id) ?? [];
            const isOpen = open === c.id;
            const ent = campaignStatus.get(c.id);
            const rawEff = statusOverride[c.id] ?? ent?.effective_status ?? '';
            const isCompleted = !statusOverride[c.id] && completedCampaigns.has(c.id);
            const effStatus = isCompleted ? 'COMPLETED' : rawEff;
            const isActive = !isCompleted && rawEff.toUpperCase().includes('ACTIVE');
            const launched = fmtDate(ent?.start_time);
            const ended = fmtDate(ent?.end_time);
            const dBudget = ent?.daily_budget != null ? Number(ent.daily_budget) : null;
            const adsUrl = adAccount
              ? `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${adAccount}&selected_campaign_ids=${c.id}`
              : null;
            return (
              <div key={c.id}>
                <div
                  onClick={() => setOpen(isOpen ? null : c.id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: COLS,
                    alignItems: 'center',
                    padding: '13px 18px',
                    borderBottom: `1px solid ${C.lineSoft}`,
                    cursor: 'pointer',
                    background: C.surface,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.hover)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.surface)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span
                      style={{
                        color: C.faint,
                        transition: 'transform .15s',
                        transform: isOpen ? 'rotate(90deg)' : 'none',
                        opacity: 1,
                      }}
                    >
                      ›
                    </span>
                    <Thumb src={thumbBy[c.id]} />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13.5,
                          fontWeight: 500,
                          color: C.primary,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {nameOverride[c.id] ?? c.name}
                      </div>
                      <div
                        style={{
                          font: `500 10.5px 'IBM Plex Mono',monospace`,
                          color: C.faint,
                          marginTop: 2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {dBudget != null ? `${sym}${abbrev(dBudget)}/day` : 'Daily budget —'}
                        {c.spend === 0 && c.impressions === 0 ? ' · no delivery yet' : ''}
                      </div>
                    </div>
                  </div>
                  <StatusCell pill={statusPill(effStatus, learningCampaigns.has(c.id))} />
                  <div
                    style={{
                      font: `500 11px 'IBM Plex Mono',monospace`,
                      color: C.muted,
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {launched ?? '—'}
                  </div>
                  <Num>{`${sym}${abbrev(c.spend)}`}</Num>
                  <Num>{c.reach ? abbrev(c.reach) : '—'}</Num>
                  <DualNum
                    value={c.linkClicks != null ? abbrev(c.linkClicks) : '—'}
                    sub={`${abbrev(c.clicks)} all`}
                  />
                  <DualNum
                    value={c.linkCtr != null ? `${c.linkCtr.toFixed(2)}%` : '—'}
                    sub={c.ctr != null ? `${c.ctr.toFixed(2)}% all` : '—'}
                  />
                  <DualNum
                    value={c.linkCpc != null ? `${sym}${c.linkCpc.toFixed(2)}` : '—'}
                    sub={c.cpc != null ? `${sym}${c.cpc.toFixed(2)} all` : '—'}
                  />
                  <NumC color={hookColor(c.hook)}>
                    {c.hook != null ? `${c.hook.toFixed(0)}%` : '—'}
                  </NumC>
                  <NumC color={hookColor(c.hold)}>
                    {c.hold != null ? `${c.hold.toFixed(0)}%` : '—'}
                  </NumC>
                  <NumC color={freqColor(c.freq)}>{c.freq != null ? c.freq.toFixed(1) : '—'}</NumC>
                </div>

                {isOpen && (
                  <div
                    style={{
                      background: C.surface2,
                      borderBottom: `1px solid ${C.lineSoft}`,
                      padding: '6px 18px 12px 42px',
                    }}
                  >
                    {/* inline rename */}
                    {renameId === c.id && (
                      <div
                        style={{
                          display: 'flex',
                          gap: 8,
                          alignItems: 'center',
                          padding: '10px 0 2px',
                        }}
                      >
                        <input
                          value={renameVal}
                          autoFocus
                          maxLength={120}
                          onChange={(e) => setRenameVal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void renameCampaign(c.id);
                            else if (e.key === 'Escape') setRenameId(null);
                          }}
                          placeholder="Campaign name"
                          style={{
                            flex: 1,
                            maxWidth: 460,
                            background: C.surface,
                            border: `1px solid ${C.basil}`,
                            borderRadius: 8,
                            padding: '8px 11px',
                            fontSize: 13,
                            color: C.primary,
                            fontFamily: 'inherit',
                          }}
                        />
                        <span
                          onClick={() => void renameCampaign(c.id)}
                          style={{
                            color: C.basil,
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: 12,
                          }}
                        >
                          {busyId === c.id ? 'Saving…' : 'Save'}
                        </span>
                        <span
                          onClick={() => setRenameId(null)}
                          style={{ color: C.muted, cursor: 'pointer', fontSize: 12 }}
                        >
                          Cancel
                        </span>
                      </div>
                    )}
                    {/* actions */}
                    <div
                      style={{
                        display: 'flex',
                        gap: 16,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        padding: '10px 0 6px',
                        fontSize: 12,
                      }}
                    >
                      {adsUrl ? (
                        <a
                          href={adsUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: C.basil, fontWeight: 600 }}
                        >
                          View in Ads Manager →
                        </a>
                      ) : (
                        <span style={{ color: C.faint }}>View link unavailable</span>
                      )}
                      {isCompleted ? (
                        <span style={{ color: C.faint }}>Schedule ended — not delivering</span>
                      ) : busyId === c.id ? (
                        <span style={{ color: C.faint, fontWeight: 600 }}>
                          {isActive ? 'Pausing…' : 'Resuming…'}
                        </span>
                      ) : confirmStatusId === c.id ? (
                        <span style={{ display: 'flex', gap: 8 }}>
                          <span
                            onClick={() => {
                              setConfirmStatusId(null);
                              void setCampaignStatus(c.id, isActive ? 'PAUSED' : 'ACTIVE');
                            }}
                            style={{
                              color: isActive ? C.watch : C.act,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            {isActive ? 'Pause spend?' : 'Start spending?'}
                          </span>
                          <span
                            onClick={() => setConfirmStatusId(null)}
                            style={{ color: C.muted, cursor: 'pointer' }}
                          >
                            Cancel
                          </span>
                        </span>
                      ) : (
                        <span
                          onClick={() => {
                            setConfirmStatusId(c.id);
                            setConfirmDelId(null);
                          }}
                          style={{
                            cursor: 'pointer',
                            color: isActive ? C.watch : C.good,
                            fontWeight: 600,
                          }}
                        >
                          {isActive ? 'Pause' : 'Resume (spend)'}
                        </span>
                      )}
                      {renameId !== c.id && (
                        <span
                          onClick={() => {
                            setRenameId(c.id);
                            setRenameVal(nameOverride[c.id] ?? c.name);
                            setConfirmDelId(null);
                            setConfirmStatusId(null);
                          }}
                          style={{ color: C.secondary, cursor: 'pointer' }}
                        >
                          Rename
                        </span>
                      )}
                      {confirmDelId === c.id ? (
                        <span style={{ display: 'flex', gap: 8 }}>
                          <span
                            onClick={() => void removeCampaign(c.id)}
                            style={{ color: C.act, fontWeight: 600, cursor: 'pointer' }}
                          >
                            Delete?
                          </span>
                          <span
                            onClick={() => setConfirmDelId(null)}
                            style={{ color: C.muted, cursor: 'pointer' }}
                          >
                            Cancel
                          </span>
                        </span>
                      ) : (
                        <span
                          onClick={() => setConfirmDelId(c.id)}
                          style={{ color: C.act, cursor: 'pointer' }}
                        >
                          Delete
                        </span>
                      )}
                      <span
                        style={{
                          color: C.faint,
                          marginLeft: 'auto',
                          font: `500 11px 'IBM Plex Mono',monospace`,
                        }}
                      >
                        {launched ? `Launched ${launched}` : 'Launch —'}
                        {ended ? ` · Ends ${ended}` : ''}
                        {dBudget != null ? ` · ${sym}${abbrev(dBudget)}/day` : ''}
                      </span>
                    </div>
                    {adsets.length > 0 && (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(160px,1fr) 78px 74px 86px 62px 58px',
                          alignItems: 'center',
                          font: `500 9px 'IBM Plex Mono',monospace`,
                          letterSpacing: '.14em',
                          color: C.faint,
                          padding: '8px 0 6px',
                        }}
                      >
                        <div>AD SETS</div>
                        <div style={{ textAlign: 'right' }}>SPEND</div>
                        <div style={{ textAlign: 'right' }}>REACH</div>
                        <div style={{ textAlign: 'right' }}>CTR</div>
                        <div style={{ textAlign: 'right' }}>HOOK</div>
                        <div style={{ textAlign: 'right' }}>HOLD</div>
                      </div>
                    )}
                    {adsets.map((a) => (
                      <div
                        key={a.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(160px,1fr) 78px 74px 86px 62px 58px',
                          alignItems: 'center',
                          padding: '9px 0',
                          borderTop: `1px solid ${C.lineSoft}`,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          <Thumb src={adsetThumbBy[a.id]} size={30} />
                          <span
                            style={{
                              fontSize: 12.5,
                              color: C.secondary,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {a.name}
                          </span>
                          {(() => {
                            const e = adsetStatus.get(a.id);
                            const pill = statusPill(
                              e?.effective_status,
                              e?.learning_stage === 'LEARNING',
                            );
                            return pill ? (
                              <span style={{ flexShrink: 0 }}>
                                <StatusPill status={pill.status} label={pill.label} />
                              </span>
                            ) : null;
                          })()}
                        </div>
                        <div
                          style={{
                            textAlign: 'right',
                            font: `500 12.5px 'Outfit',sans-serif`,
                            fontVariantNumeric: 'tabular-nums',
                            color: C.muted,
                          }}
                        >
                          {sym}
                          {abbrev(a.spend)}
                        </div>
                        <div
                          style={{
                            textAlign: 'right',
                            font: `500 12.5px 'Outfit',sans-serif`,
                            fontVariantNumeric: 'tabular-nums',
                            color: C.muted,
                          }}
                        >
                          {a.reach ? abbrev(a.reach) : '—'}
                        </div>
                        <DualNum
                          compact
                          value={a.linkCtr != null ? `${a.linkCtr.toFixed(2)}%` : '—'}
                          sub={a.ctr != null ? `${a.ctr.toFixed(2)}% all` : '—'}
                        />
                        <div
                          style={{
                            textAlign: 'right',
                            font: `600 12.5px 'Outfit',sans-serif`,
                            fontVariantNumeric: 'tabular-nums',
                            color: hookColor(a.hook),
                          }}
                        >
                          {a.hook != null ? `${a.hook.toFixed(0)}%` : '—'}
                        </div>
                        <div
                          style={{
                            textAlign: 'right',
                            font: `600 12.5px 'Outfit',sans-serif`,
                            fontVariantNumeric: 'tabular-nums',
                            color: hookColor(a.hold),
                          }}
                        >
                          {a.hold != null ? `${a.hold.toFixed(0)}%` : '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 18px',
              background: C.surface2,
              font: `500 11px 'IBM Plex Mono',monospace`,
              color: C.muted,
            }}
          >
            <span>
              SHOWING {visibleCampaigns.length}
              {visibleCampaigns.length !== live.length ? ` OF ${live.length}` : ''}
            </span>
            <span style={{ color: C.faint }}>
              ROAS &amp; booking revenue on Bookings and Launch
            </span>
          </div>
        </Card>
      )}
    </div>
  );
}

function Num({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        textAlign: 'right',
        font: `500 13.5px 'Outfit',sans-serif`,
        fontVariantNumeric: 'tabular-nums',
        color: C.secondary,
      }}
    >
      {children}
    </div>
  );
}
/** Right-aligned metric with a smaller, dimmer companion figure beneath it. */
function DualNum({ value, sub, compact }: { value: string; sub: string; compact?: boolean }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div
        style={{
          font: `500 ${compact ? 12.5 : 13.5}px 'Outfit',sans-serif`,
          fontVariantNumeric: 'tabular-nums',
          color: compact ? C.muted : C.secondary,
        }}
      >
        {value}
      </div>
      <div
        style={{
          font: `500 10px 'IBM Plex Mono',monospace`,
          fontVariantNumeric: 'tabular-nums',
          color: C.faint,
          marginTop: 2,
          whiteSpace: 'nowrap',
        }}
      >
        {sub}
      </div>
    </div>
  );
}
function NumC({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <div
      style={{
        textAlign: 'right',
        font: `600 13.5px 'Outfit',sans-serif`,
        fontVariantNumeric: 'tabular-nums',
        color,
      }}
    >
      {children}
    </div>
  );
}
function StatusCell({ pill }: { pill: { status: string; label: string } | null }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
      {pill ? (
        <StatusPill status={pill.status} label={pill.label} />
      ) : (
        <span style={{ color: C.faint, fontSize: 12 }}>—</span>
      )}
    </div>
  );
}
// A small ad thumbnail (Meta creative). Falls back to a neutral tile when the
// account has no ads_read permission or the post has no preview.
function Thumb({ src, size = 34 }: { src?: string; size?: number }) {
  if (src)
    return (
      <img
        src={src}
        alt=""
        onError={(e) => (e.currentTarget.style.visibility = 'hidden')}
        style={{
          width: size,
          height: size,
          objectFit: 'cover',
          borderRadius: 7,
          border: `1px solid ${C.line}`,
          flexShrink: 0,
        }}
      />
    );
  return (
    <div
      title="No preview available for this ad"
      style={{
        width: size,
        height: size,
        borderRadius: 7,
        border: `1px solid ${C.lineSoft}`,
        background: C.surface2,
        color: C.faint,
        fontSize: size / 2.4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      ▦
    </div>
  );
}
function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
      {label}
    </span>
  );
}
